import { useState } from 'react'
import {
  Stack, Group, Text, Badge, Button, Table, Select, Textarea, Alert, Loader,
  Tabs, ActionIcon, Tooltip,
} from '@mantine/core'
import { IconInbox, IconSend, IconCheck, IconX } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { organizationsApi, type MembershipRequest } from '@/api/organizations'
import { formatApiError } from '@/api/errors'

// Rótulo PT-BR do tipo de solicitação.
function kindLabel(r: MembershipRequest): string {
  if (r.kind === 'transfer') {
    return r.from_org_name ? `Transferência de «${r.from_org_name}»` : 'Transferência'
  }
  return 'Solicitação de entrada'
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'yellow' },
  approved: { label: 'Aprovada', color: 'green' },
  rejected: { label: 'Rejeitada', color: 'red' },
  cancelled: { label: 'Cancelada', color: 'gray' },
}

/** Invalida a fila de solicitações (ambas direções) + a lista de usuários da org. */
function useInvalidateRequests(orgId: number) {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['membership-requests', orgId] })
    qc.invalidateQueries({ queryKey: ['org-users', orgId] })
    qc.invalidateQueries({ queryKey: ['organizations'] })
  }
}

/**
 * Fila de solicitações de vínculo de uma organização (spec 2026-07-24 §3.1).
 * - incoming: pendências PARA a org (aprovar/rejeitar).
 * - outgoing: transferências que ESTA org enviou (cancelar pendentes).
 *
 * `highlightReqId` (deep-link) destaca/rola até a solicitação alvo.
 */
export function OrgRequestsSection({ orgId, highlightReqId }: { orgId: number; highlightReqId?: number | null }) {
  const [tab, setTab] = useState<string>('incoming')
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <IconInbox size={16} />
        <Text size="sm" fw={500}>Solicitações</Text>
      </Group>
      <Tabs value={tab} onChange={(v) => setTab(v ?? 'incoming')}>
        <Tabs.List>
          <Tabs.Tab value="incoming" leftSection={<IconInbox size={14} />}>Recebidas</Tabs.Tab>
          <Tabs.Tab value="outgoing" leftSection={<IconSend size={14} />}>Enviadas</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="incoming" pt="sm">
          <IncomingList orgId={orgId} highlightReqId={highlightReqId} />
        </Tabs.Panel>
        <Tabs.Panel value="outgoing" pt="sm">
          <OutgoingList orgId={orgId} highlightReqId={highlightReqId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}

/** Contagem de pendências incoming — exportada para o badge do card. */
export function useIncomingPendingCount(orgId: number | undefined, enabled = true) {
  const { data = [] } = useQuery({
    queryKey: ['membership-requests', orgId, 'incoming', 'pending'],
    queryFn: () => organizationsApi.listMembershipRequests(orgId!, 'incoming', 'pending'),
    enabled: !!orgId && enabled,
  })
  return data.length
}

function IncomingList({ orgId, highlightReqId }: { orgId: number; highlightReqId?: number | null }) {
  const invalidate = useInvalidateRequests(orgId)
  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ['membership-requests', orgId, 'incoming', 'pending'],
    queryFn: () => organizationsApi.listMembershipRequests(orgId, 'incoming', 'pending'),
  })

  // Catálogo de perfis da org — obrigatório no approve se não-vazio.
  const { data: orgTemplates = [] } = useQuery({
    queryKey: ['org-templates', orgId],
    queryFn: () => organizationsApi.getOrganizationTemplates(orgId),
  })
  const templateOptions = orgTemplates.map(t => ({ value: String(t.id), label: t.name }))

  const openApprove = (req: MembershipRequest) => {
    let template: string | null = null
    const required = templateOptions.length > 0
    modals.openConfirmModal({
      title: 'Aprovar solicitação',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Aprovar <b>{req.user_full_name || req.user_email}</b> ({kindLabel(req)})?
          </Text>
          {required ? (
            <Select
              label="Perfil da organização"
              placeholder="Selecione o perfil"
              data={templateOptions}
              defaultValue={null}
              withAsterisk
              searchable
              onChange={(v) => { template = v }}
            />
          ) : (
            <Text size="xs" c="dimmed">Sem catálogo de perfis — o colaborador manterá o perfil global.</Text>
          )}
        </Stack>
      ),
      labels: { confirm: 'Aprovar', cancel: 'Cancelar' },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        if (required && !template) {
          notifications.show({ title: 'Perfil obrigatório', message: 'Selecione um perfil do catálogo da organização.', color: 'red' })
          return
        }
        try {
          await organizationsApi.approveMembershipRequest(orgId, req.id, template != null ? Number(template) : undefined)
          invalidate()
          notifications.show({ title: 'Aprovada', message: `${req.user_full_name || req.user_email} foi aceito(a) na organização`, color: 'green' })
        } catch (err) {
          notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao aprovar solicitação'), color: 'red' })
        }
      },
    })
  }

  const openReject = (req: MembershipRequest) => {
    let note = ''
    modals.openConfirmModal({
      title: 'Rejeitar solicitação',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Rejeitar a solicitação de <b>{req.user_full_name || req.user_email}</b>?
          </Text>
          <Textarea
            label="Motivo (opcional)"
            placeholder="Explique o motivo da recusa"
            maxLength={500}
            autosize
            minRows={2}
            onChange={(e) => { note = e.currentTarget.value }}
          />
        </Stack>
      ),
      labels: { confirm: 'Rejeitar', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await organizationsApi.rejectMembershipRequest(orgId, req.id, note.trim() || undefined)
          invalidate()
          notifications.show({ title: 'Rejeitada', message: 'Solicitação rejeitada', color: 'blue' })
        } catch (err) {
          notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao rejeitar solicitação'), color: 'red' })
        }
      },
    })
  }

  if (isLoading) return <Loader size="xs" />
  if (isError) return <Alert color="red" variant="light">Falha ao carregar as solicitações. Tente novamente.</Alert>
  if (requests.length === 0) return <Text size="sm" c="dimmed">Nenhuma solicitação pendente.</Text>

  return (
    <Table fz="sm" verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Colaborador</Table.Th>
          <Table.Th>Tipo</Table.Th>
          <Table.Th>Observação</Table.Th>
          <Table.Th w={140}></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {requests.map((req) => (
          <Table.Tr
            key={req.id}
            data-testid={`request-row-${req.id}`}
            ref={(el) => {
              if (el && highlightReqId === req.id) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
            }}
            style={highlightReqId === req.id ? { backgroundColor: 'var(--mantine-color-yellow-light)' } : undefined}
          >
            <Table.Td>
              <Text size="sm">{req.user_full_name}</Text>
              <Text size="xs" c="dimmed">{req.user_email}</Text>
            </Table.Td>
            <Table.Td><Badge size="xs" variant="light">{kindLabel(req)}</Badge></Table.Td>
            <Table.Td><Text size="xs" c="dimmed">{req.note || '—'}</Text></Table.Td>
            <Table.Td>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Aprovar">
                  <ActionIcon color="green" variant="light" size="sm" onClick={() => openApprove(req)} aria-label={`Aprovar ${req.user_full_name || req.user_email}`}>
                    <IconCheck size={14} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Rejeitar">
                  <ActionIcon color="red" variant="light" size="sm" onClick={() => openReject(req)} aria-label={`Rejeitar ${req.user_full_name || req.user_email}`}>
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function OutgoingList({ orgId, highlightReqId }: { orgId: number; highlightReqId?: number | null }) {
  const invalidate = useInvalidateRequests(orgId)
  // Enviadas: todos os status para dar contexto ao gestor de origem.
  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ['membership-requests', orgId, 'outgoing', 'all'],
    queryFn: () => organizationsApi.listMembershipRequests(orgId, 'outgoing', 'all'),
  })

  const openCancel = (req: MembershipRequest) => {
    modals.openConfirmModal({
      title: 'Cancelar solicitação',
      children: (
        <Text size="sm">
          Cancelar a transferência de <b>{req.user_full_name || req.user_email}</b> para
          «{req.to_org_name}»? O colaborador permanecerá sem organização.
        </Text>
      ),
      labels: { confirm: 'Cancelar solicitação', cancel: 'Voltar' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await organizationsApi.cancelMembershipRequest(req.id)
          invalidate()
          notifications.show({ title: 'Cancelada', message: 'Solicitação cancelada', color: 'blue' })
        } catch (err) {
          notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao cancelar solicitação'), color: 'red' })
        }
      },
    })
  }

  if (isLoading) return <Loader size="xs" />
  if (isError) return <Alert color="red" variant="light">Falha ao carregar as solicitações. Tente novamente.</Alert>
  if (requests.length === 0) return <Text size="sm" c="dimmed">Nenhuma solicitação enviada.</Text>

  return (
    <Table fz="sm" verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Colaborador</Table.Th>
          <Table.Th>Destino</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th w={120}></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {requests.map((req) => {
          const st = STATUS_LABEL[req.status] ?? { label: req.status, color: 'gray' }
          return (
            <Table.Tr
              key={req.id}
              data-testid={`request-row-${req.id}`}
              style={highlightReqId === req.id ? { backgroundColor: 'var(--mantine-color-yellow-light)' } : undefined}
            >
              <Table.Td>
                <Text size="sm">{req.user_full_name}</Text>
                <Text size="xs" c="dimmed">{req.user_email}</Text>
              </Table.Td>
              <Table.Td><Text size="sm">{req.to_org_name}</Text></Table.Td>
              <Table.Td><Badge size="xs" variant="light" color={st.color}>{st.label}</Badge></Table.Td>
              <Table.Td>
                {req.status === 'pending' && (
                  <Button size="xs" variant="light" color="red" onClick={() => openCancel(req)}>Cancelar</Button>
                )}
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}
